import apiClient from '../client';
import type { PaginatedResponse } from '../../types';

export interface Contract {
  id: string;
  contractNumber: string;
  projectId: string;
  projectName: string;
  contractorId: string;
  contractorCode: string;
  contractorCompanyName: string;
  description: string;
  contractValue: number;
  paidAmount: number;
  pendingAmount: number;
  paymentProgress: number;
  startDate: string;
  endDate?: string;
  status: 'ACTIVE' | 'COMPLETED' | 'SUSPENDED' | 'TERMINATED';
  notes?: string;
  createdById?: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateContractRequest {
  contractorId: string;
  description: string;
  contractValue: number;
  startDate: string;
  endDate?: string;
  status?: string;
  notes?: string;
}

const contractService = {
  getAll: async (page = 0, size = 20) => {
    const res = await apiClient.get<PaginatedResponse<Contract>>(
      `/contracts?page=${page}&size=${size}&sort=createdAt,desc`
    );
    return res.data;
  },

  getById: async (id: string) => {
    const res = await apiClient.get<Contract>(`/contracts/${id}`);
    return res.data;
  },

  getByProject: async (projectId: string, page = 0, size = 20) => {
    const res = await apiClient.get<PaginatedResponse<Contract>>(
      `/projects/${projectId}/contracts?page=${page}&size=${size}`
    );
    return res.data;
  },

  create: async (projectId: string, data: CreateContractRequest) => {
    const res = await apiClient.post<Contract>(`/projects/${projectId}/contracts`, data);
    return res.data;
  },

  update: async (id: string, data: Partial<CreateContractRequest>) => {
    const res = await apiClient.put<Contract>(`/contracts/${id}`, data);
    return res.data;
  },

  updateStatus: async (id: string, status: string) => {
    await apiClient.put(`/contracts/${id}/status?status=${status}`);
  },

  search: async (params: { page?: number; size?: number; search?: string; status?: string; projectId?: string } = {}) => {
    const { page = 0, size = 10, search, status, projectId } = params;
    const qs = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (search) qs.set('search', search);
    if (status) qs.set('status', status);
    if (projectId) qs.set('projectId', projectId);
    const res = await apiClient.get<PaginatedResponse<Contract>>(`/contracts/search?${qs}`);
    return res.data;
  },
};

export default contractService;
